import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { HeaderWithBreadcrumbs } from "components/molecules/HeaderWithBreadcrumbs/HeaderWithBreadbrumbs";
import { useCourse, useCourseTranches } from "api/hooks";
import { CourseBadgeStatus, PageMain } from "components/molecules";
import { routes } from "consts";
import { PaziaLinkButton, PaziaLoader } from "components/atoms";
import { breadcrumbs } from "pages/breadcrumbs";
import { formatDate } from "utils";
import { StudentsTableView } from "./v2/StudentsTableView";

// Drawer-based Kursanci list view (design variant 1b). The old CourseStudentsPage.tsx variant
// was removed once this one replaced it.
export const CourseStudentsPageV2 = () => {
  const { courseId } = useParams();
  const { loading: loadingCourse, data: course } = useCourse(Number(courseId));
  const { isLoading: loadingTranches, data: tranches } = useCourseTranches(Number(courseId));

  const fields = useMemo(() => {
    return course?.forms.find((x) => x.type == 1)?.groups.flatMap((x) => x.fields)!;
  }, [course]);

  const resolveFileName = useCallback(() => {
    if (!course) return "kursanci";
    const formattedDate = course.data.dateOfCourse.toISOString().split("T")[0];
    return `${course?.data.name} - ${formattedDate}`;
  }, [course]);

  if (loadingCourse || loadingTranches) return <PaziaLoader position="center" />;
  if (!course || !tranches) return null;
  if (course.data.id !== Number(courseId)) return <PaziaLoader position="center" />;

  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={breadcrumbs.COURSE_STUDENTS(
          course.group.name,
          course.group.id,
          course.data.edition,
          course.data.id,
          course.data.variant,
        )}
        headerText="Kursanci"
      />
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800/50">
        <span className="font-semibold text-gray-900 dark:text-white">{course.data.name}</span>
        <span className="text-gray-500 dark:text-gray-400">
          <span className="font-medium">Edycja:</span> {course.data.edition}
        </span>
        {course.data.variant && (
          <span className="text-gray-500 dark:text-gray-400">
            <span className="font-medium">Wariant:</span> {course.data.variant}
          </span>
        )}
        <span className="text-gray-500 dark:text-gray-400">
          <span className="font-medium">Grupa:</span> {course.group.name}
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          <span className="font-medium">Termin:</span> {formatDate(course.data.dateOfCourse, false, false, false)} -{" "}
          {formatDate(course.data.endOfCourse)}
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          <span className="font-medium">Kursanci:</span> {course.data.numberOfStudents}
        </span>
        <CourseBadgeStatus status={course.data.status} />
      </div>
      <StudentsTableView
        key={courseId}
        courseId={Number(courseId)}
        fields={fields}
        tranches={tranches}
        exportFileName={resolveFileName()}
      />
    </PageMain>
  );
};
